import useRequest from "@/lib/request/useRequest";
import { uploadFileToBlob } from "@/lib/azure/uploadToBlob";
import {
    ImageryCompleteUploadRequest,
    ImageryUploadUrlRequest,
    ImageryUploadUrlResponse,
} from "@/types/imagery";
import FileInput from "@/components/inputs/FileInput";
import {
    Alert,
    Button,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Progress,
    Spinner,
    addToast,
} from "@heroui/react";
import { DatePicker } from "@heroui/date-picker";
import { parseDate, getLocalTimeZone, today } from "@internationalized/date";
import dayjs from "dayjs";
import { useRef, useState } from "react";
import FieldMultiSelect from "./FieldMultiSelect";

type UploadStep =
    | { phase: "form" }
    | { phase: "step1_pending" }
    | { phase: "step1_error"; errors: string[] }
    | {
        phase: "step2_uploading";
        uploadUrl: string;
        imageIds: number[];
        expiresAt: string;
        progress: number;
    }
    | {
        phase: "step2_error";
        uploadUrl: string;
        imageIds: number[];
        expiresAt: string;
        error: string;
    }
    | { phase: "step3_pending"; imageIds: number[] }
    | { phase: "step3_error"; imageIds: number[]; error: string }
    | { phase: "success" };

interface ImageryUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialFieldId: number;
    initialFieldName: string;
}

function parseUploadErrors(err: unknown): string[] {
    if (!(err instanceof Error)) return ["Unknown error"];
    try {
        const parsed = JSON.parse(err.message);
        if (Array.isArray(parsed?.errors)) return parsed.errors;
        if (typeof parsed?.error === "string") return [parsed.error];
    } catch {
        /* not JSON */
    }
    return [err.message];
}

function toErrorMessage(err: unknown): string {
    return err instanceof Error ? err.message : "An unknown error occurred";
}

function ImageryUploadModal({
    isOpen,
    onClose,
    initialFieldId,
}: ImageryUploadModalProps) {
    const { useMutateRequest } = useRequest();

    const requestUploadUrl = useMutateRequest<ImageryUploadUrlResponse>({});
    const completeUpload = useMutateRequest({});

    const [step, setStep] = useState<UploadStep>({ phase: "form" });
    const [file, setFile] = useState<File | null>(null);
    const [selectedFieldIds, setSelectedFieldIds] = useState<number[]>([
        initialFieldId,
    ]);
    const [captureDate, setCaptureDate] = useState(
        today(getLocalTimeZone()).toString(),
    );
    const abortRef = useRef<AbortController | null>(null);

    const isUploading =
        step.phase === "step1_pending" ||
        step.phase === "step2_uploading" ||
        step.phase === "step3_pending";

    const runStep2AndStep3 = async (
        uploadUrl: string,
        imageIds: number[],
        expiresAt: string,
    ) => {
        abortRef.current = new AbortController();
        setStep({ phase: "step2_uploading", uploadUrl, imageIds, expiresAt, progress: 0 });
        try {
            await uploadFileToBlob(
                uploadUrl,
                file!,
                (pct) =>
                    setStep((prev) =>
                        prev.phase === "step2_uploading"
                            ? { ...prev, progress: pct }
                            : prev,
                    ),
                abortRef.current.signal,
            );
        } catch (err) {
            setStep({
                phase: "step2_error",
                uploadUrl,
                imageIds,
                expiresAt,
                error: toErrorMessage(err),
            });
            return;
        }

        setStep({ phase: "step3_pending", imageIds });
        try {
            await completeUpload.mutateAsync({
                url: "imagery/complete_upload/",
                method: "post",
                data: {
                    image_ids: imageIds,
                    band: "NDRE",
                } satisfies ImageryCompleteUploadRequest,
            });
        } catch (err) {
            setStep({
                phase: "step3_error",
                imageIds,
                error: toErrorMessage(err),
            });
            return;
        }

        addToast({
            title: "Imagery uploaded — processing started",
            color: "success",
            timeout: 3000,
        });
        setStep({ phase: "success" });
        setTimeout(onClose, 1500);
    };

    const handleUpload = async () => {
        if (!file || selectedFieldIds.length === 0) return;

        setStep({ phase: "step1_pending" });
        let info: ImageryUploadUrlResponse;
        try {
            info = await requestUploadUrl.mutateAsync({
                url: "imagery/upload_url/",
                method: "post",
                data: {
                    field_ids: selectedFieldIds,
                    capture_date: captureDate,
                    band: "NDRE",
                } satisfies ImageryUploadUrlRequest,
            });
        } catch (err) {
            setStep({ phase: "step1_error", errors: parseUploadErrors(err) });
            return;
        }

        await runStep2AndStep3(info.upload_url, info.image_ids, info.expires_at);
    };

    const handleRetry = async () => {
        if (step.phase !== "step2_error") return;
        if (dayjs().isAfter(dayjs(step.expiresAt))) {
            setStep({ phase: "form" });
            return;
        }
        await runStep2AndStep3(step.uploadUrl, step.imageIds, step.expiresAt);
    };

    const handleClose = () => {
        abortRef.current?.abort();
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="lg">
            <ModalContent>
                <ModalHeader>Upload Imagery</ModalHeader>
                <ModalBody className="flex flex-col gap-4">
                    {(step.phase === "form" || step.phase === "step1_error") && (
                        <>
                            {step.phase === "step1_error" && (
                                <Alert
                                    color="danger"
                                    title="Upload failed"
                                    description={step.errors.join(" ")}
                                />
                            )}

                            <div className="flex flex-col gap-1">
                                <p className="text-sm font-medium">GeoTIFF file</p>
                                <FileInput
                                    size="sm"
                                    variant="flat"
                                    accept=".tif,.tiff"
                                    label={file ? file.name : "Choose .tif file..."}
                                    onSelect={(f) => { if (f instanceof File) setFile(f); }}
                                />
                            </div>

                            <div className="flex flex-col gap-1">
                                <p className="text-sm font-medium">Capture date</p>
                                <DatePicker
                                    size="sm"
                                    variant="flat"
                                    value={parseDate(captureDate)}
                                    onChange={(v) => {
                                        if (v) setCaptureDate(v.toString());
                                    }}
                                />
                            </div>

                            <div className="flex flex-col gap-1">
                                <p className="text-sm font-medium">
                                    Fields ({selectedFieldIds.length} selected)
                                </p>
                                <FieldMultiSelect
                                    selectedFieldIds={selectedFieldIds}
                                    onChange={setSelectedFieldIds}
                                />
                            </div>
                        </>
                    )}

                    {step.phase === "step1_pending" && (
                        <div className="flex flex-col items-center gap-3 py-4">
                            <Spinner size="md" />
                            <p className="text-sm text-default-500">
                                Requesting upload URL...
                            </p>
                        </div>
                    )}

                    {step.phase === "step2_uploading" && (
                        <div className="flex flex-col gap-3 py-4">
                            <Progress
                                value={step.progress}
                                label="Uploading to Azure..."
                                showValueLabel
                                color="primary"
                                className="w-full"
                            />
                            <p className="text-xs text-default-400 text-center">
                                Large files may take several minutes. Do not close this window.
                            </p>
                        </div>
                    )}

                    {step.phase === "step3_pending" && (
                        <div className="flex flex-col items-center gap-3 py-4">
                            <Spinner size="md" />
                            <p className="text-sm text-default-500">
                                Finalizing upload...
                            </p>
                        </div>
                    )}

                    {(step.phase === "step2_error" ||
                        step.phase === "step3_error") && (
                            <Alert
                                color="danger"
                                title={
                                    step.phase === "step2_error"
                                        ? "Upload failed"
                                        : "Failed to finalize upload"
                                }
                                description={step.error}
                            />
                        )}

                    {step.phase === "success" && (
                        <div className="flex flex-col items-center gap-3 py-4">
                            <p className="text-sm text-success-600 font-medium">
                                Upload complete — imagery is being processed.
                            </p>
                        </div>
                    )}
                </ModalBody>

                <ModalFooter>
                    {(step.phase === "form" || step.phase === "step1_error") && (
                        <>
                            <Button variant="flat" onPress={handleClose}>
                                Cancel
                            </Button>
                            <Button
                                color="primary"
                                onPress={handleUpload}
                                isDisabled={!file || selectedFieldIds.length === 0}
                            >
                                Upload
                            </Button>
                        </>
                    )}

                    {step.phase === "step2_error" && (
                        <>
                            <Button variant="flat" onPress={handleClose}>
                                Cancel
                            </Button>
                            <Button color="primary" onPress={handleRetry}>
                                {dayjs().isAfter(dayjs(step.expiresAt))
                                    ? "Restart Upload"
                                    : "Retry Upload"}
                            </Button>
                        </>
                    )}

                    {step.phase === "step3_error" && (
                        <>
                            <Button variant="flat" onPress={handleClose}>
                                Close
                            </Button>
                            <p className="text-xs text-default-400 self-center">
                                File was uploaded but processing failed to start.
                            </p>
                        </>
                    )}

                    {isUploading && (
                        <Button variant="flat" isDisabled>
                            Uploading...
                        </Button>
                    )}

                    {step.phase === "success" && (
                        <Button color="primary" onPress={handleClose}>
                            Done
                        </Button>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default ImageryUploadModal;
